import type { IInvoice, IInvoiceDeliveryEvent } from "../types/Invoice";

export type InvoiceIssuer = "logibud" | "accounting_provider" | "not_selected";

export type InvoiceDeliveryDocument =
  "invoice" | "invoice_copy" | "invoice_basis";

export interface InvoiceDeliveryEntry {
  channel: IInvoiceDeliveryEvent["channel"];
  recipientEmail?: string;
  sentAt?: string;
  issuanceMode: Exclude<InvoiceIssuer, "not_selected">;
  document: InvoiceDeliveryDocument;
}

export const getInvoiceIssuer = (invoice: IInvoice): InvoiceIssuer => {
  if (invoice.issuanceMode) return invoice.issuanceMode;
  if (invoice.customerSentAt || invoice.isSent) return "logibud";
  if (invoice.accountingSentAt) return "accounting_provider";
  return "not_selected";
};

const getDeliveryDocument = (
  channel: IInvoiceDeliveryEvent["channel"],
  issuanceMode: Exclude<InvoiceIssuer, "not_selected">,
): InvoiceDeliveryDocument => {
  if (channel === "customer") return "invoice";
  return issuanceMode === "accounting_provider"
    ? "invoice_basis"
    : "invoice_copy";
};

const getEventIssuer = (
  event: IInvoiceDeliveryEvent,
  fallbackIssuer: InvoiceIssuer,
): Exclude<InvoiceIssuer, "not_selected"> => {
  if (event.issuanceMode) return event.issuanceMode;
  if (event.deliveryMethod === "accounting") return "accounting_provider";
  if (fallbackIssuer !== "not_selected") return fallbackIssuer;
  return event.channel === "customer" ? "logibud" : "accounting_provider";
};

const toTimestamp = (value?: string): number => {
  if (!value) return 0;
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

export const getInvoiceDeliveryEntries = (
  invoice: IInvoice,
): InvoiceDeliveryEntry[] => {
  const issuer = getInvoiceIssuer(invoice);
  const entries: InvoiceDeliveryEntry[] = (invoice.deliveryHistory ?? []).map(
    (event) => {
      const eventIssuer = getEventIssuer(event, issuer);
      return {
        channel: event.channel,
        recipientEmail: event.recipientEmail,
        sentAt: event.sentAt,
        issuanceMode: eventIssuer,
        document: getDeliveryDocument(event.channel, eventIssuer),
      };
    },
  );

  const channelsWithHistory = new Set(entries.map((entry) => entry.channel));
  const fallbackIssuer = issuer === "not_selected" ? "logibud" : issuer;

  if (
    !channelsWithHistory.has("customer") &&
    (invoice.customerSentAt || invoice.isSent)
  ) {
    entries.push({
      channel: "customer",
      recipientEmail: invoice.lastCustomerRecipientEmail,
      sentAt: invoice.customerSentAt ?? invoice.sentAt,
      issuanceMode: "logibud",
      document: "invoice",
    });
  }

  if (!channelsWithHistory.has("accounting") && invoice.accountingSentAt) {
    entries.push({
      channel: "accounting",
      recipientEmail: invoice.lastAccountingRecipientEmail,
      sentAt: invoice.accountingSentAt,
      issuanceMode: fallbackIssuer,
      document: getDeliveryDocument("accounting", fallbackIssuer),
    });
  }

  return entries.sort(
    (left, right) => toTimestamp(right.sentAt) - toTimestamp(left.sentAt),
  );
};

export const getLatestInvoiceDelivery = (
  entries: InvoiceDeliveryEntry[],
  channel: InvoiceDeliveryEntry["channel"],
): InvoiceDeliveryEntry | undefined =>
  entries.find((entry) => entry.channel === channel);

/**
 * Once an accounting integration owns issuance, Logibud must not expose its
 * legacy Send/Resend flow for the same document. Provider delivery is managed
 * in the accounting system and its status is refreshed through reconciliation.
 *
 * Accounting-inbox deliveries without an integration record are intentionally
 * still resendable because they belong to the legacy invoice-basis flow.
 */
export const canOfferInvoiceDeliveryActions = (
  invoice: IInvoice,
  hasIntegrationAccountingRecord: boolean,
): boolean =>
  invoice.lifecycleStatus !== "discarded" &&
  !(
    getInvoiceIssuer(invoice) === "accounting_provider" &&
    hasIntegrationAccountingRecord
  );
